import React, { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Truck, Download, Loader2 } from "lucide-react";
import { ModuleTrail } from "@/components/ui/moduleBreadcrumb";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/ui/number-field";
import { Label } from "@/components/ui/label";
import { SearchInput } from "@/components/ui/search-input";
import { Pagination } from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  proveedoresAPI,
  opcionesAPI,
  ProveedorRemito,
  Proveedor,
  Sucursal,
  Deposito,
  depositoSirveSucursal,
} from "@/lib/api";
import { openPdfTab } from "@/lib/openPdfTab";
import { dataRefresh } from "@/lib/dataRefresh";
import {
  REMITO_ITEM_PAGE_SIZE_DEFAULT,
  REMITO_ITEM_PAGE_SIZE_OPTIONS,
  remitoItemPageSlice,
} from "@/lib/remitoItemPagination";
import { pedidoProveedorLineaNetoSinIva, pedidoProveedorLineaTotal, toNum } from "@/lib/utils";
import {
  comboGroupOf,
  comboVisualCodigo,
  comboVisualNombrePdf,
  comboVisualPrecioSum,
  groupComboVisualRows,
  lineNombreImpreso,
} from "@/lib/comboLine";
import { PDFPrintDialog } from "@/components/PDFPrintDialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  remitoCompraEsHistorico,
  remitoCompraEsTrabajo,
  remitoCompraPdfEsFinal,
  remitoCompraTienePdf,
} from "./remitoCompraPdf";
import { RemitoOperatorBadges } from "@/lib/RemitoOperatorBadges";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

type RemitoProductoLine = {
  codigo?: string;
  cantidad?: number;
  cantidad_total?: number;
  cantidadTotal?: number;
  cantidad_ingresada?: number;
  cantidadIngresada?: number;
  cantidad_ingresada_min?: number;
  recibido?: boolean;
  [key: string]: unknown;
};

function lineCantTotal(p: RemitoProductoLine): number {
  const total = Math.floor(toNum(p.cantidad_total ?? p.cantidadTotal ?? 0));
  if (total > 0) return total;
  // Legacy sin cantidad_total: no inventar total desde Cant. editable.
  return 0;
}

function lineCantFloor(p: RemitoProductoLine): number {
  return Math.floor(
    toNum(p.cantidad_ingresada_min ?? p.cantidad_ingresada ?? p.cantidadIngresada ?? 0),
  );
}

/** #16p: Cant. = Cant. Total en todas las líneas → usar Finalizar, no Parcial. */
function remitoCantidadesAlineadas(remito: { productos?: RemitoProductoLine[] }): boolean {
  const productos = remito.productos || [];
  if (productos.length === 0) return false;
  return productos.every((p) => {
    const cant = Math.floor(toNum(p.cantidad ?? 0));
    const total = lineCantTotal(p);
    return total > 0 && cant >= total;
  });
}

/** #16p: hay al menos una línea con Cant. > piso ya ingresado a stock (Δ > 0). */
function remitoTieneDeltaCantidad(remito: { productos?: RemitoProductoLine[] }): boolean {
  const productos = remito.productos || [];
  return productos.some((p) => {
    const cant = Math.floor(toNum(p.cantidad ?? 0));
    return cant > lineCantFloor(p);
  });
}

/**
 * Normaliza líneas al cargar:
 * - piso `cantidad_ingresada_min`
 * - Cant. editable = lo ya ingresado (como remitos vtas), NUNCA forzar Cant. Total
 * - asegura `cantidad_total` si vino del BE
 */
function normalizeRemitoProductos(
  productos?: RemitoProductoLine[],
): RemitoProductoLine[] | undefined {
  if (!productos) return productos;
  return productos.map((p) => {
    const floor = Math.floor(toNum(p.cantidad_ingresada ?? p.cantidadIngresada ?? 0));
    let total = Math.floor(toNum(p.cantidad_total ?? p.cantidadTotal ?? 0));
    // Remitos viejos: a veces solo había cantidad=total sin cantidad_total / ingresada.
    if (total <= 0) {
      const rawCant = Math.floor(toNum(p.cantidad ?? 0));
      // Si no hay piso y cantidad>0, esa cantidad era el total del pedido.
      total = floor > 0 ? Math.max(rawCant, floor) : rawCant;
    }
    return {
      ...p,
      cantidad_total: total > 0 ? total : undefined,
      cantidad_ingresada: floor,
      cantidad_ingresada_min: floor,
      // UX remitos vtas: Cant. inicia en lo ya recibido (0 si pendiente).
      cantidad: floor,
    };
  });
}

/** El buscador de adentro de cada remito queda en el código, apagado. */
const MOSTRAR_BUSCADOR_INTERNO_REMITO = false;

function draftKey(remitoId: number, codigo: string) {
  return `${remitoId}:${codigo}`;
}

function lineaCoincideProducto(producto: RemitoProductoLine, q: string) {
  const codigo = String(producto.codigo || "").toLowerCase();
  const nombre = lineNombreImpreso(producto).toLowerCase();
  const combo = String(
    producto.comboNombrePdf || producto.combo_nombre_pdf || "",
  ).toLowerCase();
  return codigo.includes(q) || nombre.includes(q) || combo.includes(q);
}

function remitoCoincideProducto(
  productos: RemitoProductoLine[] | undefined,
  q: string,
) {
  return (productos || []).some((p) => lineaCoincideProducto(p, q));
}

function applyCantDrafts(
  remitoId: number | undefined,
  productos: RemitoProductoLine[] | undefined,
  drafts: Record<string, number>,
): RemitoProductoLine[] | undefined {
  if (!productos || !remitoId) return productos;
  return productos.map((p) => {
    const codigo = String(p.codigo || "");
    const draft = drafts[draftKey(remitoId, codigo)];
    if (draft == null || !Number.isFinite(draft)) return p;
    const floor = lineCantFloor(p);
    const total = lineCantTotal(p);
    if (draft < floor) return p;
    const clamped = total > 0 ? Math.min(draft, total) : draft;
    return { ...p, cantidad: clamped };
  });
}

interface ProveedorWithRemitos {
  codigo: string;
  nombre: string;
  remitos: ProveedorRemito[];
}

/** Cantidad base para importe de línea (misma convención que antes del fix E.5 remito). */
function remitoLineaQtyValor(p: {
  cantidad?: number;
  cantidad_total?: number;
  cantidadTotal?: number;
}) {
  return toNum(p.cantidad_total ?? p.cantidadTotal ?? p.cantidad);
}

function remitoLineaPrecioUnit(p: { precioBase?: number; precio_base?: number | string }) {
  return toNum(p.precioBase ?? p.precio_base);
}

function remitoLineaDesc1(p: { descuento1?: number; descuento_1?: number }) {
  return toNum(p.descuento1 ?? p.descuento_1);
}

function remitoLineaDesc2(p: { descuento2?: number; descuento_2?: number }) {
  return toNum(p.descuento2 ?? p.descuento_2);
}

/** Neto con descuento1 → descuento2 en cadena, luego IVA 21% (misma regla F1 que PedidosForm). */
function remitoLineaNeto(p: {
  cantidad?: number;
  cantidad_total?: number;
  cantidadTotal?: number;
  precioBase?: number;
  precio_base?: number | string;
  descuento1?: number;
  descuento_1?: number;
  descuento2?: number;
  descuento_2?: number;
}) {
  return pedidoProveedorLineaTotal({
    precioBase: remitoLineaPrecioUnit(p),
    cantidad: remitoLineaQtyValor(p),
    descuento1: remitoLineaDesc1(p),
    descuento2: remitoLineaDesc2(p),
  });
}

function remitoLineaBruto(p: Parameters<typeof remitoLineaNeto>[0]) {
  return remitoLineaQtyValor(p) * remitoLineaPrecioUnit(p);
}

function remitoLineaDescuentoPesos(p: Parameters<typeof remitoLineaNeto>[0]) {
  return remitoLineaBruto(p) - pedidoProveedorLineaNetoSinIva({
    precioBase: remitoLineaPrecioUnit(p),
    cantidad: remitoLineaQtyValor(p),
    descuento1: remitoLineaDesc1(p),
    descuento2: remitoLineaDesc2(p),
  });
}

function formatMoneyAr(n: number) {
  return n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function apiErrorMessage(e: unknown, fallback: string): string {
  if (!e || typeof e !== "object") return fallback;
  const err = e as {
    message?: string;
    details?: { message?: string; detail?: string; error?: string } | string;
    data?: { message?: string; detail?: string };
  };
  if (err.details && typeof err.details === "object") {
    if (typeof err.details.message === "string" && err.details.message.trim()) {
      return err.details.message;
    }
    if (typeof err.details.detail === "string" && err.details.detail.trim()) {
      return err.details.detail;
    }
  }
  if (typeof err.message === "string" && err.message.trim()) return err.message;
  if (typeof err.details === "string" && err.details.trim()) return err.details;
  if (typeof err.data?.message === "string" && err.data.message.trim()) {
    return err.data.message;
  }
  return fallback;
}

export function ProveedoresRemitos() {
  const [searchQuery, setSearchQuery] = useState("");
  const [productoQuery, setProductoQuery] = useState("");
  const cantDraftRef = useRef<Record<string, number>>({});
  const [selectedProveedor, setSelectedProveedor] = useState<ProveedorWithRemitos | null>(null);
  const [expandedRemitos, setExpandedRemitos] = useState<Set<number>>(new Set());
  /** Buscador FE-only dentro del detalle expandido (Req 8). */
  const [detalleSearchByRemito, setDetalleSearchByRemito] = useState<Record<number, string>>({});
  const [itemPageByRemito, setItemPageByRemito] = useState<Record<number, number>>({});
  const [itemPageSizeByRemito, setItemPageSizeByRemito] = useState<Record<number, number>>({});
  const [refresh, setRefresh] = useState(0);
  const [showFinalizarModal, setShowFinalizarModal] = useState(false);
  const [showParcialModal, setShowParcialModal] = useState(false);
  const [remitoToFinalize, setRemitoToFinalize] = useState<number | null>(null);
  const [savingParcial, setSavingParcial] = useState(false);
  const [savingFinalizar, setSavingFinalizar] = useState(false);
  const [showPDFDialog, setShowPDFDialog] = useState(false);
  const [currentPDFRemito, setCurrentPDFRemito] = useState<ProveedorRemito | null>(null);

  const [remitos, setRemitos] = useState<ProveedorRemito[]>([]);
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [depositos, setDepositos] = useState<Deposito[]>([]);
  const [destinoSucursalId, setDestinoSucursalId] = useState<string>("");
  const [destinoDepositoId, setDestinoDepositoId] = useState<string>("");
  const [_loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pendiente' | 'parcial' | 'finalizado'>('all');
  const [vista, setVista] = useState<"trabajo" | "historico">("trabajo");
  const [pdfVersionPick, setPdfVersionPick] = useState<Record<string, string>>({});
  const [downloadingPDF, setDownloadingPDF] = useState<number | null>(null);

  const depositosFiltrados = useMemo(() => {
    const sid = Number(destinoSucursalId);
    if (!sid) return [];
    return depositos.filter((d) => d.activo !== false && depositoSirveSucursal(d, sid));
  }, [depositos, destinoSucursalId]);

  const remitoParaModal = remitoToFinalize
    ? remitos.find((r) => r.id === remitoToFinalize) || null
    : null;
  const finalizarCantidadesOk =
    remitoParaModal != null && remitoCantidadesAlineadas(remitoParaModal);
  const parcialBloqueadoPorCompleto =
    remitoParaModal != null && remitoCantidadesAlineadas(remitoParaModal);
  const parcialBloqueadoSinDelta =
    remitoParaModal != null && !remitoTieneDeltaCantidad(remitoParaModal);
  const destinoIncompleto = !destinoSucursalId || !destinoDepositoId;

  const preselectDestino = (sucs: Sucursal[], deps: Deposito[]) => {
    const activas = sucs.filter((s) => s.activo !== false && s.id != null);
    if (activas.length === 1 && activas[0].id != null) {
      const sid = String(activas[0].id);
      setDestinoSucursalId(sid);
      const depsSuc = deps.filter((d) => d.activo !== false && depositoSirveSucursal(d, activas[0].id));
      if (depsSuc.length === 1 && depsSuc[0].id != null) {
        setDestinoDepositoId(String(depsSuc[0].id));
      } else {
        setDestinoDepositoId("");
      }
    } else {
      setDestinoSucursalId((prev) => {
        if (prev && activas.some((s) => String(s.id) === prev)) return prev;
        return "";
      });
      setDestinoDepositoId("");
    }
  };

  const openReceiveModal = (remitoId: number, kind: "parcial" | "finalizar") => {
    setRemitoToFinalize(remitoId);
    preselectDestino(sucursales, depositos);
    if (kind === "parcial") setShowParcialModal(true);
    else setShowFinalizarModal(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [remitosData, proveedoresData, sucursalesData, depositosData] = await Promise.all([
          proveedoresAPI.getRemitos(),
          proveedoresAPI.list(),
          opcionesAPI.listSucursales(),
          opcionesAPI.listDepositos(),
        ]);
        setRemitos(
          (remitosData || []).map((r) => ({
            ...r,
            productos: applyCantDrafts(
              r.id,
              normalizeRemitoProductos(
                r.productos as RemitoProductoLine[] | undefined,
              ),
              cantDraftRef.current,
            ) as ProveedorRemito["productos"],
          })),
        );
        setProveedores(proveedoresData || []);
        setSucursales(sucursalesData || []);
        setDepositos(depositosData || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Error al cargar remitos");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [refresh]);

  // Group remitos by proveedor
  const getProveedorInfo = (proveedorId: number) => {
    return proveedores.find(p => p.id === proveedorId);
  };

  // console.log('ProveedoresRemitos render - remitos:', remitos, 'isArray:', Array.isArray(remitos));
  const SafeRemitos = Array.isArray(remitos) ? remitos : [];

  // Defensive reduction
  const remitosByProveedor = SafeRemitos.reduce((acc, remito) => {
    // Extra safety: ensure remito is valid object
    if (!remito || !remito.proveedorId) return acc;

    const prov = getProveedorInfo(remito.proveedorId);
    if (!prov) return acc;

    const key = prov.codigo;
    if (!acc[key]) {
      acc[key] = {
        codigo: prov.codigo,
        nombre: prov.nombreRazonSocial,
        remitos: []
      };
    }
    acc[key].remitos.push(remito);
    return acc;
  }, {} as Record<string, ProveedorWithRemitos>);

  const proveedoresList = Object.values(remitosByProveedor);

  const productoQ = productoQuery.trim().toLowerCase();
  const remitosConProducto = productoQ
    ? SafeRemitos.filter((r) =>
        remitoCoincideProducto(r.productos as RemitoProductoLine[] | undefined, productoQ),
      )
    : [];

  useEffect(() => {
    if (!productoQ) return;
    setExpandedRemitos((prev) => {
      const next = new Set(prev);
      for (const remito of remitosConProducto) {
        if (remito.id) next.add(remito.id);
      }
      return next;
    });
  }, [productoQ, remitos]);

  const filteredProveedores = proveedoresList.filter((proveedor) => {
    const q = searchQuery.trim().toLowerCase();
    const matchesTexto =
      !q ||
      proveedor.codigo.toLowerCase().includes(q) ||
      proveedor.nombre.toLowerCase().includes(q) ||
      proveedor.remitos.some(
        (r) =>
          (r.id?.toString().toLowerCase().includes(q) || false) ||
          (r.nroRemito?.toLowerCase().includes(q) || false),
      );
    return matchesTexto;
  });

  const forgetCantDraft = (remitoId: number) => {
    const prefix = `${remitoId}:`;
    for (const key of Object.keys(cantDraftRef.current)) {
      if (key.startsWith(prefix)) delete cantDraftRef.current[key];
    }
  };

  const toggleRemitoExpanded = (id: number) => {
    const newExpanded = new Set(expandedRemitos);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
      setItemPageByRemito((prev) => ({ ...prev, [id]: 1 }));
    }
    setExpandedRemitos(newExpanded);
  };

  const codigosDeLineaVisual = (remito: ProveedorRemito, codigoProducto: string) => {
    const productos = remito.productos || [];
    const line = productos.find((p) => p.codigo === codigoProducto);
    const group = line ? comboGroupOf(line) : null;
    if (!group) return [codigoProducto];
    const legs = productos.filter((p) => comboGroupOf(p) === group);
    return legs.length === 2 ? legs.map((p) => p.codigo) : [codigoProducto];
  };

  const handleCantidadChange = (remito: ProveedorRemito, codigoProducto: string, newCantidad: number) => {
    if (newCantidad < 0) {
      toast.error("La cantidad no puede ser negativa");
      return;
    }
    if (!remito.id || !remito.productos) return;

    const codigos = new Set(codigosDeLineaVisual(remito, codigoProducto));
    const legs = remito.productos.filter((p) => codigos.has(p.codigo)) as RemitoProductoLine[];
    const floor = Math.max(0, ...legs.map((line) => lineCantFloor(line)));
    const totals = legs.map((line) => lineCantTotal(line)).filter((n) => n > 0);
    const total = totals.length ? Math.min(...totals) : 0;
    if (newCantidad < floor) {
      toast.error("No se puede reducir la cantidad ya ingresada en un cierre parcial anterior.");
      return;
    }
    const clamped = total > 0 ? Math.min(newCantidad, total) : newCantidad;
    for (const codigo of codigos) {
      cantDraftRef.current[draftKey(remito.id, codigo)] = clamped;
    }

    setRemitos(prevRemitos => prevRemitos.map(r => {
      if (r.id === remito.id) {
        return {
          ...r,
          productos: r.productos?.map(p =>
            codigos.has(p.codigo) ? { ...p, cantidad: clamped } : p
          )
        };
      }
      return r;
    }));
  };

  const toggleProductoRecibido = (remito: ProveedorRemito, codigoProducto: string) => {
    if (!remito.id || !remito.productos) return;
    const codigos = new Set(codigosDeLineaVisual(remito, codigoProducto));
    const actual = remito.productos.find((p) => p.codigo === codigoProducto);
    const next = !actual?.recibido;

    setRemitos(prevRemitos => prevRemitos.map(r => {
      if (r.id === remito.id) {
        return {
          ...r,
          productos: r.productos?.map(p =>
            codigos.has(p.codigo) ? { ...p, recibido: next } : p
          )
        };
      }
      return r;
    }));
  };

  const handleFinalizarRemito = async () => {
    if (savingFinalizar || savingParcial) return;
    if (!remitoToFinalize) return;

    const remito = remitos.find(r => r.id === remitoToFinalize);
    if (!remito || !remito.id) {
      toast.error("Remito no encontrado");
      setShowFinalizarModal(false);
      return;
    }
    if (!remitoCantidadesAlineadas(remito)) {
      toast.error(
        "No se puede finalizar: en cada línea Cant. debe ser igual a Cant. Total (ajuste o use Guardar Parcial).",
      );
      return;
    }
    if (destinoIncompleto) {
      toast.error("Seleccione sucursal y depósito destino");
      return;
    }

    try {
      setSavingFinalizar(true);
      const today = new Date().toISOString().split('T')[0];
      const productosFinal = (remito.productos ?? []).map((p) => ({
        ...p,
        recibido: true,
      }));

      await proveedoresAPI.updateRemito(remito.id, {
        estado: "finalizado",
        fechaFinalizado: today,
        productos: productosFinal,
        sucursalId: Number(destinoSucursalId),
        depositoId: Number(destinoDepositoId),
      } as Partial<ProveedorRemito> & { sucursalId: number; depositoId: number });
      dataRefresh.emit("stock");
      dataRefresh.emit("proveedores-pedidos");
      dataRefresh.emit("remitos");

      toast.success(`Remito ${remito.nroRemito || remito.id} finalizado.`);
      setShowFinalizarModal(false);
      setRemitoToFinalize(null);
      forgetCantDraft(remito.id);
      setRefresh(prev => prev + 1);

      setTimeout(() => generateAndDownloadPDF(remito), 500);
    } catch (e) {
      console.error(e);
      toast.error(apiErrorMessage(e, "Error al finalizar remito"));
    } finally {
      setSavingFinalizar(false);
    }
  };

  const handleGuardarParcial = async () => {
    if (savingParcial || savingFinalizar) return;
    if (!remitoToFinalize) return;

    const remito = remitos.find(r => r.id === remitoToFinalize);
    if (!remito || !remito.id) {
      toast.error("Remito no encontrado");
      setShowParcialModal(false);
      setRemitoToFinalize(null);
      return;
    }

    if (remitoCantidadesAlineadas(remito)) {
      toast.error(
        "No se puede Guardar Parcial: Cant. = Cant. Total en todas las líneas. Use Finalizar Remito.",
      );
      return;
    }
    if (!remitoTieneDeltaCantidad(remito)) {
      toast.error(
        "No se puede Guardar Parcial: ninguna Cant. varió. Ajuste la diferencia de esta recepción.",
      );
      return;
    }
    if (destinoIncompleto) {
      toast.error("Seleccione sucursal y depósito destino");
      return;
    }

    try {
      setSavingParcial(true);
      await proveedoresAPI.updateRemito(remito.id, {
        productos: remito.productos,
        sucursalId: Number(destinoSucursalId),
        depositoId: Number(destinoDepositoId),
      } as Partial<ProveedorRemito> & { sucursalId: number; depositoId: number });
      dataRefresh.emit("stock");
      dataRefresh.emit("remitos");
      toast.success(`Remito ${remito.nroRemito || remito.id} guardado parcialmente (stock actualizado)`);
      setShowParcialModal(false);
      setRemitoToFinalize(null);
      forgetCantDraft(remito.id);
      setRefresh(prev => prev + 1);
    } catch (e) {
      console.error(e);
      toast.error(apiErrorMessage(e, "Error al guardar parcial"));
    } finally {
      setSavingParcial(false);
    }
  };

  const handleCancelModal = () => {
    if (savingParcial || savingFinalizar) return;
    setShowFinalizarModal(false);
    setShowParcialModal(false);
    setRemitoToFinalize(null);
  };

  const generateAndDownloadPDF = (remito: ProveedorRemito, version: string = "latest") => {
    setCurrentPDFRemito(remito);
    setPdfVersionPick((p) => ({
      ...p,
      [String(remito.id)]: version,
    }));
    setShowPDFDialog(true);
  };

  const pdfVersionDefault = (snaps: { estado?: string }[] | undefined) =>
    snaps && snaps.length > 0 ? String(snaps.length - 1) : "latest";

  const pdfVersionOptions = (snaps: { estado?: string }[] | undefined) => {
    let parcialN = 0;
    return (snaps || []).map((s, i) => {
      const isFinal = remitoCompraPdfEsFinal(s.estado);
      if (!isFinal) parcialN += 1;
      return {
        value: String(i),
        label: isFinal ? "Cierre final" : `Parcial #${parcialN}`,
      };
    });
  };

  const handlePDFDownload = async () => {
    if (!currentPDFRemito || !currentPDFRemito.id) {
      toast.error("No se puede descargar: remito sin ID");
      return;
    }

    const snaps = currentPDFRemito.pdfSnapshots || currentPDFRemito.pdf_snapshots || [];
    const version =
      pdfVersionPick[String(currentPDFRemito.id)] ?? pdfVersionDefault(snaps);

    try {
      setDownloadingPDF(currentPDFRemito.id);
      const blob = await proveedoresAPI.downloadProveedorRemitoPdf(currentPDFRemito.id, version);
      if (!blob) {
        toast.error("No se pudo generar el PDF del remito");
        return;
      }
      const win = await openPdfTab(blob, undefined, {
        path: `/api/proveedores-remitos/${currentPDFRemito.id}/pdf/`,
        query: version ? `version=${encodeURIComponent(version)}` : undefined,
      });
      if (!win) {
        toast.error("Pop-up bloqueado. Habilitá pop-ups para este sitio.");
      } else {
        toast.success("PDF del remito abierto en nueva pestaña");
      }
    } catch (error: unknown) {
      const message =
        error && typeof error === "object" && "message" in error
          ? String((error as { message?: unknown }).message || "")
          : "";
      toast.error(message || "Error al descargar PDF del remito");
    } finally {
      setDownloadingPDF(null);
    }
  };

  const handlePDFPrint = () => {
    // Similar to download but print
    // ... implementation omitted for brevity, reusing download logic structure if needed or keeping mocked print
    if (!currentPDFRemito || !currentPDFRemito.id) return;
    // ... logic
    toast.info("Imprimiendo...");
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-AR');
  };

  const getProveedorTotals = (remitos: ProveedorRemito[]) => {
    const pendientes = remitos.filter(r => r.estado === 'pendiente' || r.estado === 'parcial').length;
    const finalizados = remitos.filter(r => r.estado === 'finalizado').length;
    const total = remitos.reduce((sum, r) => sum + Number(r.importe || 0), 0);
    return { pendientes, finalizados, total };
  };

  // Render proveedor detail view
  const renderProveedorDetail = () => {
    if (!selectedProveedor) return null;

    // Get ALL remitos for this proveedor from live state (for summary counts)
    const allProveedorRemitos = remitos.filter(r => {
      const prov = getProveedorInfo(r.proveedorId);
      return prov?.codigo === selectedProveedor.codigo;
    });
    const { pendientes, finalizados, total } = getProveedorTotals(allProveedorRemitos);

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3">
            <h2 className="text-2xl font-bold leading-tight">{selectedProveedor.nombre}</h2>
            <ModuleTrail
              crumbs={[
                { label: "Remitos", onClick: () => setSelectedProveedor(null) },
                { label: selectedProveedor.nombre },
              ]}
            />
          </div>
          <Tabs
            value={vista}
            onValueChange={(value) => {
              setVista(value as "trabajo" | "historico");
              setStatusFilter("all");
            }}
            className="ml-auto shrink-0 gap-0"
          >
            <TabsList>
              <TabsTrigger value="trabajo">En curso</TabsTrigger>
              <TabsTrigger value="historico">Histórico</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Summary Cards - Clickable to filter */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => setStatusFilter('all')}
            className={`border rounded-lg p-4 bg-card text-left transition-all hover:scale-[1.02] ${statusFilter === 'all' ? 'ring-2 ring-primary border-primary' : 'border-input'
              }`}
          >
            <p className="text-sm text-muted-foreground mb-1">Total Remitos</p>
            <p className="text-2xl font-bold">{allProveedorRemitos.length}</p>
          </button>
          <button
            onClick={() => {
              setVista("trabajo");
              setStatusFilter("pendiente");
            }}
            className={`border rounded-lg p-4 bg-card text-left transition-all hover:scale-[1.02] ${statusFilter === 'pendiente' ? 'ring-2 ring-yellow-500 border-yellow-500' : 'border-input'
              }`}
          >
            <p className="text-sm text-muted-foreground mb-1">Pendientes</p>
            <p className="text-2xl font-bold text-yellow-600">{pendientes}</p>
          </button>
          <button
            onClick={() => {
              setVista("historico");
              setStatusFilter("finalizado");
            }}
            className={`border rounded-lg p-4 bg-card text-left transition-all hover:scale-[1.02] ${statusFilter === 'finalizado' ? 'ring-2 ring-green-500 border-green-500' : 'border-input'
              }`}
          >
            <p className="text-sm text-muted-foreground mb-1">Finalizados</p>
            <p className="text-2xl font-bold text-green-600">{finalizados}</p>
          </button>
          <div className="border border-input rounded-lg p-4 bg-card">
            <p className="text-sm text-muted-foreground mb-1">Importe Total</p>
            <p className="text-2xl font-bold">${total.toLocaleString('es-AR')}</p>
          </div>
        </div>

        <div className="max-w-md">
          <SearchInput
            placeholder="Buscar producto por código o nombre..."
            value={productoQuery}
            onChange={(e) => setProductoQuery(e.target.value)}
          />
        </div>

        {/* Remitos List - Use LIVE remitos state filtered by proveedor */}
        <div className="space-y-3">
          {(() => {
            // Get fresh remitos for this proveedor from live state, filtered by status
            const proveedorRemitos = remitos.filter(r => {
              const prov = getProveedorInfo(r.proveedorId);
              const matchesProveedor = prov?.codigo === selectedProveedor.codigo;
              const matchesVista =
                vista === "historico"
                  ? remitoCompraEsHistorico(r.estado)
                  : remitoCompraEsTrabajo(r.estado);
              const matchesStatus =
                statusFilter === 'all' ||
                r.estado === statusFilter ||
                (statusFilter === 'pendiente' && r.estado === 'parcial') ||
                (statusFilter === 'finalizado' && remitoCompraEsHistorico(r.estado));
              const matchesProducto =
                !productoQ ||
                remitoCoincideProducto(r.productos as RemitoProductoLine[] | undefined, productoQ);
              return matchesProveedor && matchesVista && matchesStatus && matchesProducto;
            });
            if (proveedorRemitos.length === 0) {
              return (
                <div className="p-8 text-center text-muted-foreground border border-input rounded-lg">
                  {productoQ
                    ? "Ningún remito de esta vista tiene ese producto."
                    : vista === "historico"
                      ? "No hay remitos finalizados en el histórico"
                      : "No hay remitos en curso"}
                </div>
              );
            }
            return proveedorRemitos.map((remito) => (
              <div key={remito.id} className="border border-input rounded-lg overflow-hidden">
                {/* Remito header: evitar <button> que envuelva el botón PDF (nested button / React warning). */}
                <div className="flex w-full items-stretch bg-muted/50 hover:bg-muted transition-colors">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (remito.id) toggleRemitoExpanded(remito.id);
                    }}
                    onKeyDown={(e) => {
                      if (!remito.id) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleRemitoExpanded(remito.id);
                      }
                    }}
                    className="flex flex-1 min-w-0 items-center justify-between gap-3 px-6 py-4 text-left cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <Truck className="h-5 w-5 shrink-0 text-orange-600" />
                      <div className="min-w-0 text-left">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold">{remito.nroRemito || remito.id}</span>
                          <span className="text-xs text-muted-foreground">→</span>
                          <span className="rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            {remito.nroPedido || "N/A"}
                          </span>
                          <span
                            className={`rounded px-2 py-0.5 text-xs ${
                              remito.estado === "finalizado"
                                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                : remito.estado === "parcial"
                                  ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                                  : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                            }`}
                          >
                            {remito.estado === "finalizado"
                              ? "Finalizado"
                              : remito.estado === "parcial"
                                ? "Parcial"
                                : "Pendiente"}
                          </span>
                        </div>
                        <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          <span>
                            {formatDate(remito.fechaCreacion)} • {remito.productos?.length || 0}{" "}
                            producto{(remito.productos?.length || 0) !== 1 ? "s" : ""}
                          </span>
                          <RemitoOperatorBadges source={remito} />
                        </div>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-lg font-semibold">
                        ${Number(remito.importe || 0).toLocaleString("es-AR")}
                      </div>
                      {remito.fechaFinalizado && (
                        <div className="text-xs text-muted-foreground">
                          Finalizado: {formatDate(remito.fechaFinalizado)}
                        </div>
                      )}
                    </div>
                  </div>
                  {remitoCompraTienePdf(remito) && (
                    <div
                      className="flex shrink-0 flex-col items-end justify-center border-l border-border/60 px-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        disabled={downloadingPDF === remito.id}
                        onClick={() => {
                          const snaps = remito.pdfSnapshots || remito.pdf_snapshots || [];
                          const v =
                            pdfVersionPick[String(remito.id)] ??
                            pdfVersionDefault(snaps);
                          generateAndDownloadPDF(remito, v);
                        }}
                        className="h-8 w-8"
                        title="Descargar PDF (Parcial #N o cierre final)"
                      >
                        {downloadingPDF === remito.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                      {(remito.pdfSnapshots?.length || remito.pdf_snapshots?.length || 0) > 1 ? (
                        <Select
                          value={
                            pdfVersionPick[String(remito.id)] ??
                            pdfVersionDefault(remito.pdfSnapshots || remito.pdf_snapshots)
                          }
                          onValueChange={(v) =>
                            setPdfVersionPick((p) => ({
                              ...p,
                              [String(remito.id)]: v,
                            }))
                          }
                        >
                          <SelectTrigger
                            size="sm"
                            aria-label="Versión del PDF del remito de compra"
                            title="Historial: parciales y cierre final (default = más actual)"
                            className="mt-0.5 h-7 w-[9.5rem] border-input bg-background px-2 text-[11px] text-muted-foreground shadow-none"
                            onPointerDown={(e) => e.stopPropagation()}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {pdfVersionOptions(
                              remito.pdfSnapshots || remito.pdf_snapshots,
                            ).map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : null}
                    </div>
                  )}
                </div>

                {/* Remito Products */}
                {remito.id && expandedRemitos.has(remito.id) && (() => {
                  const qInterno = MOSTRAR_BUSCADOR_INTERNO_REMITO
                    ? (detalleSearchByRemito[remito.id] || "").trim().toLowerCase()
                    : "";
                  const q = qInterno || productoQ;
                  const filas = groupComboVisualRows(remito.productos || []).filter((row) => {
                    if (!q) return true;
                    return row.legs.some((producto) =>
                      lineaCoincideProducto(producto as RemitoProductoLine, q),
                    );
                  });
                  const paged = remitoItemPageSlice(
                    filas,
                    itemPageByRemito[remito.id] ?? 1,
                    itemPageSizeByRemito[remito.id] ?? REMITO_ITEM_PAGE_SIZE_DEFAULT,
                  );
                  return (
                  <div className="px-2 md:px-6 py-4 bg-background">
                    {MOSTRAR_BUSCADOR_INTERNO_REMITO ? (
                    <div className="mb-3 max-w-md">
                      <SearchInput
                        placeholder="Buscar producto por código o nombre..."
                        value={detalleSearchByRemito[remito.id] || ""}
                        onChange={(e) => {
                          setDetalleSearchByRemito((prev) => ({
                            ...prev,
                            [remito.id!]: e.target.value,
                          }));
                          setItemPageByRemito((prev) => ({ ...prev, [remito.id!]: 1 }));
                        }}
                      />
                    </div>
                    ) : null}
                    <div className="border border-border rounded-lg overflow-x-auto">
                      <table className="w-full text-sm min-w-[500px]">
                        <thead className="bg-muted">
                          <tr>
                            <th className="text-center py-2 px-2 md:px-4 font-medium w-12 md:w-16">Recibido</th>
                            <th className="text-left py-2 px-2 md:px-4 font-medium">Código</th>
                            <th className="text-left py-2 px-2 md:px-4 font-medium">Producto</th>
                            <th className="text-center py-2 px-2 md:px-4 font-medium w-16 md:w-20">Cant.</th>
                            <th className="text-center py-2 px-2 md:px-4 font-medium w-16 md:w-20">Cant. Total</th>
                            <th className="text-right py-2 px-2 md:px-4 font-medium hidden md:table-cell">Precio Base</th>
                            <th className="text-center py-2 px-2 md:px-4 font-medium hidden lg:table-cell">Desc. %</th>
                            <th className="text-center py-2 px-2 md:px-4 font-medium hidden lg:table-cell">Desc. %</th>
                            <th className="text-right py-2 px-2 md:px-4 font-medium hidden xl:table-cell">Desc. $</th>
                            <th className="text-right py-2 px-2 md:px-4 font-medium">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                            {filas.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={10}
                                    className="py-6 px-4 text-center text-muted-foreground"
                                  >
                                    {q
                                      ? "No hay productos que coincidan con la búsqueda"
                                      : "Sin productos en este remito"}
                                  </td>
                                </tr>
                            ) : (
                            paged.rows.map((row) => {
                            const producto = row.legs[0];
                            const codigo = row.isCombo ? comboVisualCodigo(row.legs) : producto.codigo;
                            const nombre = row.isCombo ? comboVisualNombrePdf(row.legs) : lineNombreImpreso(producto);
                            const floors = row.legs.map((leg) => lineCantFloor(leg as RemitoProductoLine));
                            const floor = Math.max(0, ...floors);
                            const totals = row.legs
                              .map((leg) => lineCantTotal(leg as RemitoProductoLine))
                              .filter((n) => n > 0);
                            const total = totals.length ? Math.min(...totals) : 0;
                            const precio = row.isCombo
                              ? comboVisualPrecioSum(row.legs.map((leg) => ({
                                precioBase: remitoLineaPrecioUnit(leg),
                              })))
                              : remitoLineaPrecioUnit(producto);
                            const descPesos = row.legs.reduce(
                              (s, leg) => s + remitoLineaDescuentoPesos(leg),
                              0,
                            );
                            const totalLinea = row.legs.reduce((s, leg) => s + remitoLineaNeto(leg), 0);
                            return (
                            <tr key={row.key} className="border-t border-border">
                              <td className="py-2 px-4 text-center">
                                <div className="flex justify-center">
                                  <Checkbox
                                    checked={row.legs.every((leg) => leg.recibido)}
                                    onCheckedChange={() => toggleProductoRecibido(remito, producto.codigo)}
                                    disabled={remito.estado === 'finalizado'}
                                  />
                                </div>
                              </td>
                              <td className="py-2 px-4 whitespace-pre-line">{codigo}</td>
                              <td className="py-2 px-2 md:px-4 whitespace-normal break-words">{nombre}</td>
                              <td className="py-2 px-2 md:px-4 text-center">
                                <NumberField
                                  min={floor}
                                  max={total || undefined}
                                  value={Math.floor(toNum(producto.cantidad))}
                                  onChange={(e) => handleCantidadChange(remito, producto.codigo, parseInt(e.target.value) || 0)}
                                  disabled={remito.estado === 'finalizado'}
                                  className="w-16 md:w-20 h-7 text-center px-1 md:px-2 py-1"
                                />
                              </td>
                              <td className="py-2 px-2 md:px-4 text-center">
                                {total ||
                                  producto.cantidad_total ||
                                  producto.cantidadTotal ||
                                  "—"}
                              </td>
                              <td className="py-2 px-2 md:px-4 text-right hidden md:table-cell tabular-nums">
                                ${formatMoneyAr(precio)}
                              </td>
                              <td className="py-2 px-2 md:px-4 text-center hidden lg:table-cell">
                                {remitoLineaDesc1(producto) || 0}%
                              </td>
                              <td className="py-2 px-2 md:px-4 text-center hidden lg:table-cell">
                                {remitoLineaDesc2(producto) || 0}%
                              </td>
                              <td className="py-2 px-2 md:px-4 text-right hidden xl:table-cell text-muted-foreground tabular-nums">
                                ${formatMoneyAr(descPesos)}
                              </td>
                              <td className="py-2 px-2 md:px-4 text-right font-medium tabular-nums">
                                ${formatMoneyAr(totalLinea)}
                              </td>
                            </tr>
                            );
                            })
                            )}
                        </tbody>
                      </table>
                    </div>
                    {paged.total > 0 ? (
                      <Pagination
                        currentPage={paged.page}
                        totalCount={paged.total}
                        pageSize={paged.pageSize}
                        onPageChange={(page) =>
                          setItemPageByRemito((prev) => ({ ...prev, [remito.id!]: page }))
                        }
                        onPageSizeChange={(size) => {
                          setItemPageSizeByRemito((prev) => ({ ...prev, [remito.id!]: size }));
                          setItemPageByRemito((prev) => ({ ...prev, [remito.id!]: 1 }));
                        }}
                        pageSizeOptions={[...REMITO_ITEM_PAGE_SIZE_OPTIONS]}
                      />
                    ) : null}
                    {remitoCompraEsTrabajo(remito.estado) ? (
                      <div className="mt-3 flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          aria-disabled={
                            remitoCantidadesAlineadas(remito) || !remitoTieneDeltaCantidad(remito)
                          }
                          title={
                            remitoCantidadesAlineadas(remito)
                              ? "Cant. = Cant. Total en todas las líneas: use Finalizar Remito."
                              : !remitoTieneDeltaCantidad(remito)
                                ? "Ninguna Cant. varió: ajuste la diferencia de esta recepción."
                                : undefined
                          }
                          onClick={() => {
                            if (!remito.id) return;
                            // Como remitos vtas: toast + no abrir modal si bloqueado.
                            if (remitoCantidadesAlineadas(remito)) {
                              toast.error(
                                "No se puede Guardar Parcial: Cant. = Cant. Total en todas las líneas. Use Finalizar Remito.",
                              );
                              return;
                            }
                            if (!remitoTieneDeltaCantidad(remito)) {
                              toast.error(
                                "No se puede Guardar Parcial: ninguna Cant. varió. Ajuste la diferencia de esta recepción.",
                              );
                              return;
                            }
                            openReceiveModal(remito.id, "parcial");
                          }}
                          className={`text-xs ${
                            remitoCantidadesAlineadas(remito) || !remitoTieneDeltaCantidad(remito)
                              ? "opacity-50"
                              : ""
                          }`}
                        >
                          Guardar Parcial
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            if (!remito.id) return;
                            if (!remitoCantidadesAlineadas(remito)) {
                              toast.error(
                                "No se puede finalizar: en cada línea Cant. debe ser igual a Cant. Total (ajuste o use Guardar Parcial).",
                              );
                              return;
                            }
                            openReceiveModal(remito.id, "finalizar");
                          }}
                          disabled={!remitoCantidadesAlineadas(remito)}
                          title={
                            !remitoCantidadesAlineadas(remito)
                              ? "Ajuste cada Cant. para que sea igual a Cant. Total en todas las líneas (o use Guardar Parcial)."
                              : undefined
                          }
                          className="text-xs bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:pointer-events-auto"
                        >
                          Finalizar Remito
                        </Button>
                      </div>
                    ) : remitoCompraTienePdf(remito) ? (
                      <div className="mt-3 flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => generateAndDownloadPDF(remito)}
                          className="text-xs"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Descargar PDF
                        </Button>
                      </div>
                    ) : null}
                  </div>
                  );
                })()}
              </div>
            ))
          })()}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    // If viewing proveedor detail
    if (selectedProveedor) {
      return renderProveedorDetail();
    }

    // Default view: Grid of proveedores
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Seleccione un Proveedor</h3>

        <div className="max-w-md">
          <SearchInput
            placeholder="Buscar por proveedor, código, remito o pedido..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProveedores.map((proveedor) => {
            const { pendientes, finalizados, total } = getProveedorTotals(proveedor.remitos);

            return (
              <div
                key={proveedor.codigo}
                className="border border-input rounded-lg p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => setSelectedProveedor(proveedor)}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold">{proveedor.nombre}</h4>
                  <Truck className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Código: {proveedor.codigo}
                </p>
                <div className="mt-3 pt-3 border-t border-input space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Remitos:</span>
                    <span className="font-medium">{proveedor.remitos.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Pendientes:</span>
                    <span className="font-medium text-yellow-600">{pendientes}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Finalizados:</span>
                    <span className="font-medium text-green-600">{finalizados}</span>
                  </div>
                  <div className="flex justify-between text-sm pt-2 border-t border-input">
                    <span className="font-semibold">Total:</span>
                    <span className="font-bold text-lg">${total.toLocaleString('es-AR')}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredProveedores.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            {searchQuery.trim() ? 'No se encontraron resultados' : 'No hay remitos generados'}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="space-y-4 mt-4">
        {renderContent()}
      </div>

      {/* Finalizar Remito Modal */}
      <Dialog open={showFinalizarModal} onOpenChange={(open) => {
        if (!open) handleCancelModal();
      }}>
        <DialogContent
          onInteractOutside={(e) => {
            if (savingFinalizar) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (savingFinalizar) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Finalizar Remito</DialogTitle>
            <DialogDescription>
              Indique sucursal y depósito destino. Se sumará al stock la diferencia de esta
              recepción y el remito quedará finalizado.
              {remitoParaModal && !finalizarCantidadesOk ? (
                <>
                  <br /><br />
                  <span className="text-destructive font-medium">
                    No se puede finalizar mientras Cant. y Cant. Total difieran en alguna línea.
                    Ajuste las cantidades o use Guardar Parcial.
                  </span>
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          {finalizarCantidadesOk && (
            <div className="grid gap-3 py-2">
              <div className="grid gap-1.5">
                <Label htmlFor="fin-sucursal">Sucursal destino</Label>
                <Select
                  value={destinoSucursalId || undefined}
                  onValueChange={(v) => {
                    setDestinoSucursalId(v);
                    const depsSuc = depositos.filter(
                      (d) => d.activo !== false && depositoSirveSucursal(d, Number(v)),
                    );
                    setDestinoDepositoId(
                      depsSuc.length === 1 && depsSuc[0].id != null ? String(depsSuc[0].id) : "",
                    );
                  }}
                  disabled={savingFinalizar}
                >
                  <SelectTrigger id="fin-sucursal" className="w-full">
                    <SelectValue placeholder="Seleccionar sucursal" />
                  </SelectTrigger>
                  <SelectContent>
                    {sucursales
                      .filter((s) => s.activo !== false && s.id != null)
                      .map((s) => (
                        <SelectItem key={s.id} value={String(s.id)}>
                          {s.nombre}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="fin-deposito">Depósito destino</Label>
                <Select
                  value={destinoDepositoId || undefined}
                  onValueChange={setDestinoDepositoId}
                  disabled={savingFinalizar || !destinoSucursalId}
                >
                  <SelectTrigger id="fin-deposito" className="w-full">
                    <SelectValue placeholder="Seleccionar depósito" />
                  </SelectTrigger>
                  <SelectContent>
                    {depositosFiltrados.map((d) => (
                      <SelectItem key={d.id} value={String(d.id)}>
                        {d.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelModal} disabled={savingFinalizar}>
              Cancelar
            </Button>
            <Button
              onClick={handleFinalizarRemito}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:pointer-events-auto"
              disabled={!finalizarCantidadesOk || destinoIncompleto || savingFinalizar}
              title={
                !finalizarCantidadesOk
                  ? "Cant. debe coincidir con Cant. Total en todas las líneas."
                  : destinoIncompleto
                    ? "Seleccione sucursal y depósito destino"
                    : undefined
              }
            >
              {savingFinalizar ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Finalizando…
                </>
              ) : (
                "Finalizar Remito"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Guardar Parcial Modal */}
      <Dialog open={showParcialModal} onOpenChange={(open) => {
        if (!open) handleCancelModal();
      }}>
        <DialogContent
          onInteractOutside={(e) => {
            if (savingParcial) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (savingParcial) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Guardar Estado Parcial</DialogTitle>
            <DialogDescription>
              {parcialBloqueadoPorCompleto ? (
                <>
                  No se puede guardar parcial: Cant. = Cant. Total en todas las líneas. Use{" "}
                  <span className="font-semibold text-foreground">Finalizar Remito</span>.
                </>
              ) : parcialBloqueadoSinDelta ? (
                <>
                  No se puede guardar parcial: ninguna Cant. varió respecto a lo ya ingresado.
                  Ajuste la diferencia de esta recepción.
                </>
              ) : (
                <>
                  Se guardará el estado parcial y se sumará al stock la diferencia de esta
                  recepción en la sucursal/depósito indicados.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {!parcialBloqueadoPorCompleto && !parcialBloqueadoSinDelta && (
            <div className="grid gap-3 py-2">
              <div className="grid gap-1.5">
                <Label htmlFor="par-sucursal">Sucursal destino</Label>
                <Select
                  value={destinoSucursalId || undefined}
                  onValueChange={(v) => {
                    setDestinoSucursalId(v);
                    const depsSuc = depositos.filter(
                      (d) => d.activo !== false && depositoSirveSucursal(d, Number(v)),
                    );
                    setDestinoDepositoId(
                      depsSuc.length === 1 && depsSuc[0].id != null ? String(depsSuc[0].id) : "",
                    );
                  }}
                  disabled={savingParcial}
                >
                  <SelectTrigger id="par-sucursal" className="w-full">
                    <SelectValue placeholder="Seleccionar sucursal" />
                  </SelectTrigger>
                  <SelectContent>
                    {sucursales
                      .filter((s) => s.activo !== false && s.id != null)
                      .map((s) => (
                        <SelectItem key={s.id} value={String(s.id)}>
                          {s.nombre}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="par-deposito">Depósito destino</Label>
                <Select
                  value={destinoDepositoId || undefined}
                  onValueChange={setDestinoDepositoId}
                  disabled={savingParcial || !destinoSucursalId}
                >
                  <SelectTrigger id="par-deposito" className="w-full">
                    <SelectValue placeholder="Seleccionar depósito" />
                  </SelectTrigger>
                  <SelectContent>
                    {depositosFiltrados.map((d) => (
                      <SelectItem key={d.id} value={String(d.id)}>
                        {d.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelModal} disabled={savingParcial}>
              Cancelar
            </Button>
            <Button
              onClick={handleGuardarParcial}
              disabled={
                parcialBloqueadoPorCompleto ||
                parcialBloqueadoSinDelta ||
                destinoIncompleto ||
                savingParcial
              }
              className="disabled:opacity-50"
            >
              {savingParcial ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Guardando…
                </>
              ) : (
                "Guardar Parcial"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Print Dialog */}
      <PDFPrintDialog
        open={showPDFDialog}
        onOpenChange={setShowPDFDialog}
        onDownload={handlePDFDownload}
        onPrint={handlePDFPrint}
        title="Remito Generado"
        description="¿Desea descargar o imprimir el remito?"
      />
    </>
  );
}